import { Button, Group, Modal, Select, Stack, TextInput } from '@mantine/core'
import { notifications } from '@mantine/notifications'
import { normalizeErrorString } from '@medplum/core'
import { IconCirclePlus } from '@tabler/icons-react'
import { useEffect, useState } from 'react'
import { useMiddlewareClient } from '@/hooks/useMiddlewareClient'
import {
  upsertInsurancePayer,
  type InsurancePayerOption,
  type UpsertInsurancePayerPayload,
} from '@/pages/Settings/insurancePlans.service'
import { states } from '@/utils/states'

type CustomInsurancePayerModalProps = {
  opened: boolean
  onClose: () => void
  onSuccess: (payer: InsurancePayerOption) => void
  initialValue?: InsurancePayerOption | null
}

const EMPTY_VALUES: UpsertInsurancePayerPayload = {
  payerId: '',
  payerName: '',
  state: '',
}

export function CustomInsurancePayerModal({
  opened,
  onClose,
  onSuccess,
  initialValue,
}: CustomInsurancePayerModalProps) {
  const middlewareClient = useMiddlewareClient()
  const [values, setValues] = useState<UpsertInsurancePayerPayload>(EMPTY_VALUES)
  const [isSaving, setIsSaving] = useState(false)

  useEffect(() => {
    if (!opened) {
      return
    }

    setValues(
      initialValue
        ? {
            payerParamId: initialValue.parameterId,
            payerId: initialValue.payerId,
            payerName: initialValue.payerName,
            state: initialValue.state ?? '',
          }
        : EMPTY_VALUES,
    )
  }, [initialValue, opened])

  const handleSubmit = async () => {
    if (!values.payerId.trim() || !values.payerName.trim() || !values.state.trim()) {
      notifications.show({
        title: 'Missing fields',
        message: 'Payer ID, payer name, and state are required.',
        color: 'red',
      })
      return
    }

    setIsSaving(true)
    try {
      const result = await upsertInsurancePayer(middlewareClient, {
        payerParamId: values.payerParamId,
        payerId: values.payerId.trim(),
        payerName: values.payerName.trim(),
        state: values.state.trim().toUpperCase(),
      })

      onSuccess({
        id: values.payerParamId || result.parameterId,
        payerId: values.payerId.trim(),
        payerName: values.payerName.trim(),
        state: values.state.trim().toUpperCase(),
        source: 'custom',
        editable: true,
        deletable: true,
        parameterId: values.payerParamId || result.parameterId,
      })

      notifications.show({
        title: values.payerParamId ? 'Payer updated' : 'Payer created',
        message: values.payerParamId
          ? 'Custom insurance payer updated successfully.'
          : 'Custom insurance payer created successfully.',
        color: 'green',
      })
      onClose()
    } catch (error) {
      notifications.show({
        title: values.payerParamId ? 'Payer not updated' : 'Payer not created',
        message: normalizeErrorString(error) || 'Failed to save custom insurance payer.',
        color: 'red',
      })
    } finally {
      setIsSaving(false)
    }
  }

  return (
    <Modal
      opened={opened}
      onClose={() => {
        if (!isSaving) {
          onClose()
        }
      }}
      title={initialValue ? 'Edit custom insurance payer' : 'Create custom insurance payer'}
    >
      <Stack>
        <TextInput
          label="Payer name"
          placeholder="Enter payer name"
          value={values.payerName}
          onChange={(event) =>
            setValues((current) => ({ ...current, payerName: event.currentTarget.value }))
          }
        />
        <TextInput
          label="Payer ID"
          placeholder="Enter payer ID"
          value={values.payerId}
          onChange={(event) =>
            setValues((current) => ({ ...current, payerId: event.currentTarget.value }))
          }
        />
        <Select
          label="State"
          placeholder="Select a state"
          data={states.map((item) => ({ value: item.value, label: item.label }))}
          value={values.state}
          onChange={(nextValue) => setValues((current) => ({ ...current, state: nextValue ?? '' }))}
          searchable
        />
        <Group justify="flex-end">
          <Button variant="default" onClick={onClose} disabled={isSaving}>
            Cancel
          </Button>
          <Button
            leftSection={<IconCirclePlus size={16} />}
            onClick={() => void handleSubmit()}
            loading={isSaving}
          >
            {initialValue ? 'Update payer' : 'Create payer'}
          </Button>
        </Group>
      </Stack>
    </Modal>
  )
}
